import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { createUserNotification } from './notificationService';

function requireDb() {
  if (!db) {
    throw new Error('Firestore is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

function timestampValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  return Number(new Date(value)) || 0;
}

export async function getModeratorProfile(uid) {
  requireDb();
  const snapshot = await getDoc(doc(db, 'moderators', uid));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function listReportsForModerator() {
  requireDb();
  const snapshot = await getDocs(collection(db, 'reports'));
  return snapshot.docs
    .map((entry) => ({ id: entry.id, ...entry.data() }))
    .sort((a, b) => timestampValue(b.updatedAt || b.createdAt) - timestampValue(a.updatedAt || a.createdAt));
}

export async function listModerationActionsForModerator() {
  requireDb();
  const snapshot = await getDocs(collection(db, 'moderationActions'));
  return snapshot.docs
    .map((entry) => ({ id: entry.id, ...entry.data() }))
    .sort((a, b) => timestampValue(b.createdAt) - timestampValue(a.createdAt));
}

async function createModerationAction({ moderatorId, reportId, targetType, targetId, action, note }) {
  await addDoc(collection(db, 'moderationActions'), {
    moderatorId,
    reportId: reportId || '',
    targetType,
    targetId,
    action,
    note: String(note || '').trim(),
    createdAt: serverTimestamp(),
  });
}

export async function updateReportReview(report, status, resolution, moderatorId) {
  requireDb();
  const reportId = typeof report === 'string' ? report : report.id;
  await updateDoc(doc(db, 'reports', reportId), {
    status,
    moderatorId,
    resolution: String(resolution || '').trim(),
    updatedAt: serverTimestamp(),
  });
  await createModerationAction({
    moderatorId,
    reportId,
    targetType: typeof report === 'string' ? 'problemDoc' : report.targetType,
    targetId: typeof report === 'string' ? report : report.targetId,
    action: `status:${status}`,
    note: resolution,
  });
  if (typeof report !== 'string' && report.reporterId) {
    await createUserNotification(report.reporterId, {
      actorId: moderatorId,
      actorName: 'Moderator',
      type: 'moderationReportUpdated',
      title: 'Report updated',
      body: `Your report about ${report.targetType} was marked ${status}.`,
      href: '/dashboard/notifications',
    });
  }
}

function splitTargetId(targetId, expectedParts) {
  const parts = String(targetId || '').split('/').filter(Boolean);
  if (parts.length !== expectedParts) {
    throw new Error('Report target id does not match the expected path format.');
  }
  return parts;
}

export async function hideModerationTarget(report, moderatorId, note = '') {
  requireDb();
  const now = serverTimestamp();
  if (report.targetType === 'problemDoc') {
    await updateDoc(doc(db, 'problemDocs', report.targetId), { visibility: 'private', updatedAt: now });
  } else if (report.targetType === 'pageEntry') {
    await updateDoc(doc(db, 'pageEntries', report.targetId), { visibility: 'private', updatedAt: now });
  } else if (report.targetType === 'profile') {
    await updateDoc(doc(db, 'profiles', report.targetId), { visibility: 'private', updatedAt: now });
  } else {
    throw new Error('This report target cannot be hidden. Delete the reported message/comment instead.');
  }
  await createModerationAction({
    moderatorId,
    reportId: report.id,
    targetType: report.targetType,
    targetId: report.targetId,
    action: 'hide-target',
    note: note || 'Target visibility set to private by moderator.',
  });
}

export async function deleteModerationTarget(report, moderatorId, note = '') {
  requireDb();
  if (report.targetType === 'problemComment') {
    const [problemId, commentId] = splitTargetId(report.targetId, 2);
    await deleteDoc(doc(db, 'problemComments', problemId, 'comments', commentId));
  } else if (report.targetType === 'pageComment') {
    const [entryId, commentId] = splitTargetId(report.targetId, 2);
    await deleteDoc(doc(db, 'pageComments', entryId, 'comments', commentId));
  } else if (report.targetType === 'problemMessage') {
    const [requestId, messageId] = splitTargetId(report.targetId, 2);
    await deleteDoc(doc(db, 'problemContactMessages', requestId, 'messages', messageId));
  } else {
    throw new Error('This report target cannot be deleted from moderation. Hide the target instead.');
  }
  await createModerationAction({
    moderatorId,
    reportId: report.id,
    targetType: report.targetType,
    targetId: report.targetId,
    action: 'delete-target',
    note: note || 'Reported comment/message deleted by moderator.',
  });
}
